import { useRef, useEffect, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MapPin } from 'lucide-react';
import { mapboxMapHideLogoClass } from '../../utils/mapboxRtl';
import {
  acquireSharedMap,
  onSharedMapReady,
  releaseSharedMap,
  registerMapCleanup,
} from '../../utils/sharedMapInstance';
import logger from '@/utils/logger';

/**
 * Composant de carte interactive pour la construction de circuits
 * Utilise Mapbox GL JS pour la sélection d'arrêts et la visualisation des trajets
 */
const CircuitBuilderMap = ({
  stops = [],
  selectedStops = [],
  optimizedOrder = [],
  inboundOrder = [],
  onStopSelection,
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const routeLayerRef = useRef(null);
  const popupRef = useRef(null);
  // Cadrage initial sur l'école/arrêts : flag PAR MONTAGE (la carte est partagée
  // et persiste entre les ouvertures, donc on ne peut pas le stocker sur l'objet map).
  const initialFitDoneRef = useRef(false);

  // Initialiser la carte — instance Mapbox partagée (reuseMap), pas de map load
  // supplémentaire à chaque visite des assignations. Voir utils/sharedMapInstance.js.
  useEffect(() => {
    if (map.current) return; // Éviter la réinitialisation

    // Pas de center initial ici : le cadrage réel est fait par fitBounds sur
    // l'école + arrêts (effet plus bas). Évite un déplacement caméra Rabat→école
    // superflu (et les requêtes de tuiles Mapbox annulées au retour sur le builder).
    const owner = {};
    map.current = acquireSharedMap(mapContainer.current, {}, owner);

    let cancelled = false;
    onSharedMapReady(() => {
      if (cancelled) return;
      logger.debug('🗺️ [CircuitBuilderMap] Carte prête');
      setMapLoaded(true);
    });

    // Filet de sécurité au release : retirer les listeners de couches de ce
    // composant (la purge générique enlève déjà les couches/sources/popups).
    registerMapCleanup(() => {
      const eventTypes = ['click', 'mouseenter', 'mouseleave'];
      const layerIds = [
        'school-selected',
        'stops-selected',
        'stops-available',
        'stops-assigned',
        'stops-inactive',
        'stops-labels',
      ];
      eventTypes.forEach((et) =>
        layerIds.forEach((lid) => {
          try {
            map.current?.off(et, lid);
          } catch {
            /* ignore */
          }
        }),
      );
    });

    return () => {
      cancelled = true;
      try {
        popupRef.current?.remove();
      } catch {
        /* ignore */
      }
      releaseSharedMap(owner);
      map.current = null;
      if (!cancelled) setMapLoaded(false);
    };
  }, []);

  // Ajouter les sources et layers pour les arrêts
  const addStopLayers = useCallback(() => {
    if (!map.current || !mapLoaded) return;

    // Supprimer les layers existants dans le bon ordre (layers avant sources)
    const layersToRemove = [
      'school-selected',
      'stops-selected',
      'stops-available',
      'stops-assigned',
      'stops-inactive',
      'stops-unselected',
      'stops-labels',
    ];
    layersToRemove.forEach((layerId) => {
      if (map.current.getLayer(layerId)) {
        try {
          map.current.removeLayer(layerId);
        } catch (error) {
          logger.warn(`⚠️ Erreur suppression layer ${layerId}:`, error);
        }
      }
    });

    // Supprimer la source seulement après avoir supprimé tous les layers
    if (map.current.getSource('stops')) {
      try {
        map.current.removeSource('stops');
      } catch (error) {
        logger.warn('⚠️ Erreur suppression source stops:', error);
      }
    }

    // Préparer les données GeoJSON
    const stopsGeoJSON = {
      type: 'FeatureCollection',
      features: stops
        .map((stop) => {
          const isSelected = selectedStops.some((s) => s.id === stop.id);
          const orderIndex = isSelected ? inboundOrder.findIndex((s) => s.id === stop.id) : null;

          // Count only non-school stops to align numbering with modal and GPS suivi
          const nonSchoolLabel =
            orderIndex !== null && !stop.isSchool
              ? inboundOrder.slice(0, orderIndex + 1).filter((s) => !s.isSchool).length
              : null;

          return {
            type: 'Feature',
            properties: {
              id: stop.id,
              name: stop.name || stop.stop_name || 'Arrêt',
              address: stop.address || 'Adresse non disponible',
              student_count: stop.student_count || 0,
              isSelected,
              isSchool: stop.isSchool || false,
              orderIndex,
              orderLabel: nonSchoolLabel !== null ? nonSchoolLabel.toString() : '',
              assignmentStatus: stop.assignmentStatus || 'available',
              isAssigned: stop.isAssigned || false,
              assignedToRoutes: stop.assignedToRoutes || [],
              assignedToInactiveCircuits: stop.assignedToInactiveCircuits || false,
              status: stop.status || 'active',
            },
            geometry: {
              type: 'Point',
              coordinates: [parseFloat(stop.longitude), parseFloat(stop.latitude)],
            },
          };
        })
        .filter((feature) => feature.geometry.coordinates[0] && feature.geometry.coordinates[1]),
    };

    // Ajouter la source avec gestion d'erreur
    try {
      map.current.addSource('stops', {
        type: 'geojson',
        data: stopsGeoJSON,
      });
    } catch (error) {
      logger.warn('⚠️ Erreur ajout source stops:', error);
      // Si la source existe déjà, mettre à jour ses données
      if (map.current.getSource('stops')) {
        map.current.getSource('stops').setData(stopsGeoJSON);
      }
    }

    // Ajouter les layers avec gestion d'erreur
    const layersConfig = [
      {
        id: 'school-selected',
        type: 'circle',
        source: 'stops',
        filter: ['all', ['==', ['get', 'isSelected'], true], ['==', ['get', 'isSchool'], true]],
        paint: {
          'circle-radius': 24,
          'circle-color': '#F59E0B',
          'circle-stroke-width': 4,
          'circle-stroke-color': '#ffffff',
        },
      },
      {
        id: 'stops-selected',
        type: 'circle',
        source: 'stops',
        filter: ['all', ['==', ['get', 'isSelected'], true], ['!=', ['get', 'isSchool'], true]],
        paint: {
          'circle-radius': 20,
          'circle-color': '#3B82F6',
          'circle-stroke-width': 3,
          'circle-stroke-color': '#ffffff',
        },
      },
      {
        id: 'stops-available',
        type: 'circle',
        source: 'stops',
        filter: [
          'all',
          ['==', ['get', 'isSelected'], false],
          ['==', ['get', 'assignmentStatus'], 'available'],
          ['!=', ['get', 'status'], 'inactive'],
        ],
        paint: {
          'circle-radius': 16,
          'circle-color': '#10B981',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      },
      {
        id: 'stops-assigned',
        type: 'circle',
        source: 'stops',
        filter: [
          'all',
          ['==', ['get', 'isSelected'], false],
          ['==', ['get', 'assignmentStatus'], 'assigned'],
          ['!=', ['get', 'status'], 'inactive'],
        ],
        paint: {
          'circle-radius': 16,
          'circle-color': '#EF4444',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      },
      {
        id: 'stops-inactive',
        type: 'circle',
        source: 'stops',
        filter: ['==', ['get', 'status'], 'inactive'],
        paint: {
          'circle-radius': 16,
          'circle-color': '#9CA3AF',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      },
      {
        id: 'stops-labels',
        type: 'symbol',
        source: 'stops',
        layout: {
          'text-field': ['case', ['!=', ['get', 'orderLabel'], ''], ['get', 'orderLabel'], '📍'],
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': 12,
          'text-allow-overlap': true,
          'text-ignore-placement': true,
        },
        paint: {
          'text-color': '#ffffff',
        },
      },
    ];

    // Ajouter chaque layer avec gestion d'erreur
    layersConfig.forEach((layerConfig) => {
      try {
        if (!map.current.getLayer(layerConfig.id)) {
          map.current.addLayer(layerConfig);
        }
      } catch (error) {
        logger.warn(`⚠️ Erreur ajout layer ${layerConfig.id}:`, error);
      }
    });

    // Supprimer les anciens event listeners pour éviter les doublons
    const eventTypes = ['click', 'mouseenter', 'mouseleave'];
    const layerIds = [
      'school-selected',
      'stops-selected',
      'stops-available',
      'stops-assigned',
      'stops-inactive',
      'stops-labels',
    ];
    eventTypes.forEach((eventType) => {
      layerIds.forEach((layerId) => {
        map.current.off(eventType, layerId);
      });
    });

    // Événements de clic
    // Gestionnaire de clic pour école sélectionnée (orange) - non désélectionnable
    map.current.on('click', 'school-selected', (e) => {
      const feature = e.features[0];
      logger.debug('🏫 [CircuitBuilderMap] Clic sur ÉCOLE - non désélectionnable:', {
        stopId: feature.properties.id,
        stopName: feature.properties.name,
      });
      // L'école ne peut pas être désélectionnée - aucun appel à onStopSelection
    });

    map.current.on('click', 'stops-selected', (e) => {
      const feature = e.features[0];
      const stop = stops.find((s) => s.id === feature.properties.id);
      logger.debug('🗺️ [CircuitBuilderMap] Clic sur arrêt SÉLECTIONNÉ:', {
        stopId: feature.properties.id,
        stopName: feature.properties.name,
        isSelected: true,
        totalSelectedStops: selectedStops.length,
      });
      if (stop && onStopSelection) {
        onStopSelection(stop, true);
      }
    });

    // Gestionnaires de clic pour arrêts disponibles (verts)
    map.current.on('click', 'stops-available', (e) => {
      const feature = e.features[0];
      const stop = stops.find((s) => s.id === feature.properties.id);
      logger.debug('🗺️ [CircuitBuilderMap] Clic sur arrêt DISPONIBLE:', {
        stopId: feature.properties.id,
        stopName: feature.properties.name,
        isSelected: false,
        assignmentStatus: 'available',
        totalSelectedStops: selectedStops.length,
      });
      if (stop && onStopSelection) {
        onStopSelection(stop, false);
      }
    });

    // Gestionnaires de clic pour arrêts assignés (rouges)
    map.current.on('click', 'stops-assigned', (e) => {
      const feature = e.features[0];
      const stop = stops.find((s) => s.id === feature.properties.id);
      logger.debug('🗺️ [CircuitBuilderMap] Clic sur arrêt ASSIGNÉ:', {
        stopId: feature.properties.id,
        stopName: feature.properties.name,
        isSelected: false,
        assignmentStatus: 'assigned',
        assignedToRoutes: feature.properties.assignedToRoutes,
        totalSelectedStops: selectedStops.length,
      });
      if (stop && onStopSelection) {
        onStopSelection(stop, false);
      }
    });

    // Curseur pointer sur les arrêts
    map.current.on('mouseenter', 'school-selected', () => {
      map.current.getCanvas().style.cursor = 'not-allowed';
    });
    map.current.on('mouseenter', 'stops-selected', () => {
      map.current.getCanvas().style.cursor = 'pointer';
    });
    map.current.on('mouseenter', 'stops-available', () => {
      map.current.getCanvas().style.cursor = 'pointer';
    });
    map.current.on('mouseenter', 'stops-assigned', () => {
      map.current.getCanvas().style.cursor = 'pointer';
    });
    map.current.on('mouseleave', 'school-selected', () => {
      map.current.getCanvas().style.cursor = '';
    });
    map.current.on('mouseleave', 'stops-selected', () => {
      map.current.getCanvas().style.cursor = '';
    });
    map.current.on('mouseleave', 'stops-available', () => {
      map.current.getCanvas().style.cursor = '';
    });
    map.current.on('mouseleave', 'stops-assigned', () => {
      map.current.getCanvas().style.cursor = '';
    });
    map.current.on('mouseenter', 'stops-inactive', () => {
      map.current.getCanvas().style.cursor = 'not-allowed';
    });
    map.current.on('mouseleave', 'stops-inactive', () => {
      map.current.getCanvas().style.cursor = '';
    });

    // Créer le popup une seule fois si nécessaire
    if (!popupRef.current) {
      popupRef.current = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
      });
    }

    const showPopup = (e) => {
      const feature = e.features[0];
      if (!feature) return;

      const coordinates = feature.geometry.coordinates.slice();
      const props = feature.properties;

      const assignedToRoutes = Array.isArray(props.assignedToRoutes)
        ? props.assignedToRoutes
        : typeof props.assignedToRoutes === 'string'
          ? (() => {
              try {
                return JSON.parse(props.assignedToRoutes);
              } catch {
                return [];
              }
            })()
          : [];

      // Utiliser assignmentStatus (source de vérité) au lieu de isAssigned
      const assignmentInfo =
        props.assignmentStatus === 'assigned' && assignedToRoutes.length > 0
          ? `<p class="text-xs text-red-600 mt-1">🔴 Assigné à: ${assignedToRoutes
              .map((r) => (typeof r === 'object' && r !== null ? r.name || r.code || '' : String(r)))
              .join(', ')}</p>`
          : props.isSchool
            ? `<p class="text-xs text-orange-600 mt-1">🏫 École</p>`
            : props.assignedToInactiveCircuits
              ? `<p class="text-xs text-green-600 mt-1">🟢 Disponible (assigné à circuit inactif)</p>`
              : `<p class="text-xs text-green-600 mt-1">🟢 Disponible</p>`;

      popupRef.current
        .setLngLat(coordinates)
        .setHTML(
          `
          <div class="p-2">
            <h3 class="font-semibold text-sm">${props.isSchool ? '🏫 ' : ''}${props.name}</h3>
            <p class="text-xs text-gray-600">${props.address}</p>
            ${props.student_count ? `<p class="text-xs text-blue-600 mt-1">${props.student_count} élève(s)</p>` : ''}
            ${assignmentInfo}
            <p class="text-xs text-gray-500 mt-1">
              ${props.isSchool ? '🏫 École' : props.isSelected ? '✅ Sélectionné' : '📍 Cliquer pour sélectionner'}
            </p>
          </div>
        `,
        )
        .addTo(map.current);
    };

    const hidePopup = () => {
      if (popupRef.current) {
        popupRef.current.remove();
      }
    };

    map.current.on('mouseenter', 'school-selected', showPopup);
    map.current.on('mouseenter', 'stops-selected', showPopup);
    map.current.on('mouseenter', 'stops-available', showPopup);
    map.current.on('mouseenter', 'stops-assigned', showPopup);
    map.current.on('mouseenter', 'stops-inactive', (e) => {
      const feature = e.features[0];
      if (!feature) return;
      const props = feature.properties;
      const coordinates = feature.geometry.coordinates.slice();
      popupRef.current
        .setLngLat(coordinates)
        .setHTML(
          `<div class="p-2">
            <h3 class="font-semibold text-sm">${props.name}</h3>
            <p class="text-xs text-gray-600">${props.address}</p>
            <p class="text-xs text-gray-400 mt-1">🚫 Arrêt désactivé</p>
          </div>`,
        )
        .addTo(map.current);
    });
    map.current.on('mouseenter', 'stops-labels', (e) => {
      const feature = e.features[0];
      map.current.getCanvas().style.cursor = feature?.properties?.isSchool ? 'not-allowed' : 'pointer';
      showPopup(e);
    });
    map.current.on('mouseleave', 'school-selected', hidePopup);
    map.current.on('mouseleave', 'stops-selected', hidePopup);
    map.current.on('mouseleave', 'stops-available', hidePopup);
    map.current.on('mouseleave', 'stops-assigned', hidePopup);
    map.current.on('mouseleave', 'stops-inactive', hidePopup);
    map.current.on('mouseleave', 'stops-labels', () => {
      map.current.getCanvas().style.cursor = '';
      hidePopup();
    });
  }, [mapLoaded, stops, selectedStops, inboundOrder, onStopSelection]);

  // Adapter la taille de la carte quand le conteneur change (ex: drawer, layout)
  useEffect(() => {
    if (!map.current || !mapContainer.current) return;

    const container = mapContainer.current;
    const resizeObserver = new ResizeObserver(() => {
      if (map.current) {
        map.current.resize();
      }
    });
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [mapLoaded]);

  // Mettre à jour les layers des arrêts
  useEffect(() => {
    if (!mapLoaded || !map.current) return;

    logger.debug('🗺️ [CircuitBuilderMap] Mise à jour des layers:', {
      stopsCount: stops.length,
      selectedCount: selectedStops.length,
      optimizedCount: optimizedOrder.length,
    });

    // Ajouter/mettre à jour les layers
    addStopLayers();

    // Forcer un resize après mise à jour du DOM (adaptation au drawer)
    const resizeId = requestAnimationFrame(() => {
      if (map.current) {
        map.current.resize();
      }
    });

    // Ajuster la vue pour inclure tous les arrêts (une fois par ouverture du builder)
    if (stops.length > 0 && !initialFitDoneRef.current) {
      const validStops = stops.filter((stop) => stop.latitude && stop.longitude);
      if (validStops.length > 0) {
        const bounds = new mapboxgl.LngLatBounds();
        validStops.forEach((stop) => {
          bounds.extend([parseFloat(stop.longitude), parseFloat(stop.latitude)]);
        });

        // animate:false → cadrage instantané au lieu d'un vol animé : évite de
        // charger/annuler les tuiles des zooms intermédiaires (requêtes ERR_ABORTED).
        map.current.fitBounds(bounds, {
          padding: 50,
          maxZoom: 15,
          animate: false,
        });

        // Marquer que le fit initial a été fait (pour ce montage)
        initialFitDoneRef.current = true;
      }
    }

    return () => {
      cancelAnimationFrame(resizeId);
    };
  }, [mapLoaded, stops, selectedStops, optimizedOrder, addStopLayers]);

  // Dessiner le trajet optimisé
  useEffect(() => {
    if (!mapLoaded || !map.current || inboundOrder.length < 2) {
      // Supprimer le trajet existant s'il y en a un
      if (routeLayerRef.current && map.current.getLayer('route')) {
        map.current.removeLayer('route');
        map.current.removeSource('route');
        routeLayerRef.current = null;
      }
      return;
    }

    logger.debug('🛣️ [CircuitBuilderMap] Dessin du trajet (ordre matin):', inboundOrder.length, 'arrêts');

    // Créer les coordonnées du trajet dans l'ordre matin
    const coordinates = inboundOrder
      .filter((stop) => stop.latitude && stop.longitude)
      .map((stop) => [parseFloat(stop.longitude), parseFloat(stop.latitude)]);

    if (coordinates.length < 2) return;

    // Supprimer le trajet existant
    if (map.current.getLayer('route')) {
      map.current.removeLayer('route');
    }
    if (map.current.getSource('route')) {
      map.current.removeSource('route');
    }

    // Ajouter la source du trajet
    map.current.addSource('route', {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: coordinates,
        },
      },
    });

    // Ajouter la couche du trajet
    map.current.addLayer({
      id: 'route',
      type: 'line',
      source: 'route',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': '#3B82F6',
        'line-width': 4,
        'line-opacity': 0.8,
      },
    });

    routeLayerRef.current = 'route';
  }, [mapLoaded, inboundOrder]);

  return (
    <div className="relative w-full h-full min-h-[600px]">
      {/* Carte */}
      <div ref={mapContainer} className={`w-full h-full ${mapboxMapHideLogoClass}`} />

      {/* Overlay d'informations */}
      <div className="absolute top-4 left-4 bg-white rounded-lg shadow-lg p-4 max-w-sm">
        <div className="flex items-center space-x-2 mb-3">
          <MapPin className="text-blue-600" size={20} />
          <h3 className="font-semibold text-gray-900">Sélection d'Arrêts</h3>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Arrêts disponibles:</span>
            <span className="font-medium">{stops.length}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Arrêts sélectionnés:</span>
            <span className="font-medium text-blue-600">{selectedStops.length}</span>
          </div>
          {optimizedOrder.length > 1 && (
            <div className="flex justify-between">
              <span className="text-gray-600">Ordre manuel:</span>
              <span className="font-medium text-green-600">✓</span>
            </div>
          )}
        </div>

        {selectedStops.length > 0 && (
          <div className="mt-3 pt-3 border-t">
            <p className="text-xs text-gray-500">💡 Cliquez sur un arrêt pour le sélectionner/désélectionner</p>
          </div>
        )}
      </div>

      {/* Légende */}
      <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-3">
        <h4 className="font-medium text-gray-900 mb-2 text-sm">Légende</h4>
        <div className="space-y-2 text-xs">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded-full bg-green-500"></div>
            <span>🟢 Arrêt disponible</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded-full bg-red-500"></div>
            <span>🔴 Arrêt déjà assigné</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded-full bg-blue-600"></div>
            <span>✅ Arrêt sélectionné</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded-full bg-gray-400"></div>
            <span>
              <span className="text-gray-400">●</span> Arrêt Inactif
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 rounded-full bg-orange-500"></div>
            <span>🏫 École</span>
          </div>
        </div>
      </div>

      {/* Indicateur de chargement */}
      {!mapLoaded && (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-gray-600 text-sm">Chargement de la carte...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CircuitBuilderMap;
